import { useEffect, useState } from 'react'

import { Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import IconCalendar from 'src/icons/IconCalendar'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconCircle from 'src/icons/IconCircle'
import IconEmail from 'src/icons/IconEmail'
import IconPhone from 'src/icons/IconPhone'
import { PRIORITY_CONFIG } from 'src/lib/taskPriority'
import { cn } from 'src/utils'

import type { AgendaItem } from '../lib/agendaItems'

/**
 * One agenda line. Purely presentational — every side effect is a callback, so
 * the row (and the view above it) render in Storybook and tests with no Apollo,
 * router or context providers.
 *
 * Anatomy, left to right:
 *   [time] [priority | calendar] { name / subtitle } [AI pill] [complete] [action]
 *
 * Deliberate omissions, each one the result of a review round:
 * - No "Open profile" button. The name is the only navigation.
 * - No per-type tinting or colored left edge. Every row is the same surface.
 * - No checkbox and no overflow menu.
 * - Start time only. Duration is in the subtitle.
 *
 * The leading slot shows a task's *priority*, never its type. Type used to live
 * here, which put a phone glyph on the left of a call task and a second phone
 * on the right — two identical icons doing different jobs, and only the left
 * one turned into a tick. Priority on the left removes the collision and means
 * the right-hand icon is unambiguously "the action".
 */
export interface AgendaRowProps {
  item: AgendaItem
  /**
   * Opening the applicant needs `routes` + the applicant drawer, both of which
   * are container concerns. `newTab` carries the meta-key intent so the
   * container can apply the repo's `window.open` idiom.
   */
  onOpenApplicant?: (applicantId: string, options: { newTab: boolean }) => void
  /** Fires the row's trailing action (place a call / write an email). */
  onAction?: (item: AgendaItem) => void
  /**
   * Completes the task. May return a promise: if it rejects, the row rolls its
   * optimistic tick back. The mutation and the error toast live in the
   * container.
   */
  onComplete?: (item: AgendaItem) => void | Promise<unknown>
  /**
   * Opens the task itself for editing. Clicking a task row was previously dead
   * space — the only way in was the applicant name, which opens the applicant,
   * not the task.
   */
  onOpenTask?: (item: AgendaItem) => void
  /**
   * Names the owner ahead of the subtitle. On for any scope covering more than
   * one person; off for a single person's agenda, where the name would be on
   * every row saying nothing the control does not already say.
   */
  showOwner?: boolean
  /**
   * Suppresses the time because the row above holds the same one.
   *
   * The voice agent books ten-minute slots across the whole team, so two people
   * holding 9:40 AM is ordinary — but printing it twice reads as a duplicated
   * row, which is the most bug-looking thing about a merged list. The cell is
   * still rendered, so the glyph column stays straight.
   */
  hideTime?: boolean
  /**
   * Whether the trailing icons do anything.
   *
   * The phone dials from *your* device and the tick completes the task, so on a
   * list covering other people both are one stray click from acting on work that
   * is not yours. Off leaves the slots empty rather than removing them, which is
   * what keeps the icon column aligned.
   */
  canAct?: boolean
}

// Trailing action icons sit a step smaller than the text they sit beside, so a
// column of them reads as quiet affordances rather than a row of buttons.
const ACTION_ICON_CLASS = 'h-3.5 w-3.5 flex-shrink-0 fill-none'
const GLYPH_CLASS = 'h-4 w-4 flex-shrink-0 fill-none'

/**
 * Leading slot. Bookings get a calendar (they have no priority); tasks get
 * their priority bars from the shared PRIORITY_CONFIG, so the indicator matches
 * the Tasks page and the old dashboard rows.
 */
const LeadingGlyph = ({ item }: { item: AgendaItem }) => {
  if (item.kind === 'BOOKING') {
    return (
      <IconCalendar
        className={cn(GLYPH_CLASS, 'stroke-doubleNickel-gray-400')}
      />
    )
  }

  const priorityConfig = item.priority
    ? PRIORITY_CONFIG[item.priority as keyof typeof PRIORITY_CONFIG]
    : undefined

  if (priorityConfig) {
    const { Icon, color } = priorityConfig
    return <Icon className={cn('h-4 w-4 flex-shrink-0', color)} />
  }

  // No priority recorded — a neutral dot keeps the column aligned.
  return (
    <IconCircle className="h-2 w-2 flex-shrink-0 fill-doubleNickel-gray-300 stroke-doubleNickel-gray-300" />
  )
}

const AgendaRow = ({
  item,
  onOpenApplicant,
  onAction,
  onComplete,
  onOpenTask,
  showOwner = false,
  hideTime = false,
  canAct = true,
}: AgendaRowProps) => {
  // Optimistic completion: the tick reads as done immediately, and the row
  // waits ~300ms so the dim/strikethrough transition is visible before the
  // mutation (and any refetch) lands.
  const [optimisticallyCompleted, setOptimisticallyCompleted] = useState(false)

  useEffect(() => {
    setOptimisticallyCompleted(false)
  }, [item.id, item.isCompleted])

  const isCompleted = item.isCompleted || optimisticallyCompleted
  // A cancelled or no-show booking is struck for the same reason a completed
  // task is: it is no longer something to do.
  const isStruck = isCompleted || item.isCancelled
  const isDimmed = item.isDimmed || optimisticallyCompleted
  const isOverdue = item.overdueDueLabel !== null
  const canComplete = item.isCompletable && !optimisticallyCompleted && canAct
  // An overdue row leads with the date it was actually due, so it has no plain
  // time to suppress — and two carryovers from different days are not a repeat.
  const suppressTime = hideTime && !isOverdue

  const handleComplete = () => {
    setOptimisticallyCompleted(true)
    setTimeout(() => {
      const result = onComplete?.(item)
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        ;(result as Promise<unknown>).catch(() => {
          setOptimisticallyCompleted(false)
        })
      }
    }, 300)
  }

  // Tasks carry a title, a description and a reminder that the two-line row
  // can't hold. The hover card mirrors the one on the existing dashboard task
  // rows so the detail lives in the same place across both surfaces.
  const hoverCard =
    item.kind === 'TASK' ? (
      <div className="flex max-w-xs flex-col gap-1.5 whitespace-normal">
        <div className="break-words text-xs font-medium">{item.subtitle}</div>
        {item.description && (
          <div className="break-words text-xs text-doubleNickel-gray-400">
            {item.description}
          </div>
        )}
        <div className="mt-1 flex flex-col gap-1 border-t border-doubleNickel-gray-300 pt-1.5">
          <div className="text-xs text-doubleNickel-gray-400">
            Due: {dayjs(item.at).format('MMM D, YYYY h:mm A')}
          </div>
          {item.reminderDate && (
            <div className="text-xs text-doubleNickel-gray-400">
              Reminder: {dayjs(item.reminderDate).format('MMM D, YYYY h:mm A')}
            </div>
          )}
        </div>
        {item.applicantName && (
          <div className="mt-1 flex flex-col gap-1 border-t border-doubleNickel-gray-300 pt-1.5">
            <div className="text-xs text-doubleNickel-gray-400">
              Applicant: {item.applicantName}
            </div>
          </div>
        )}
      </div>
    ) : null

  // Clicking the row opens the thing itself: the task for a task, the applicant
  // for a booking (matching BookingCard). The name still goes to the applicant
  // in both cases, so a task row offers both destinations.
  const handleRowClick = (event: React.MouseEvent) => {
    // Nested controls (name, complete, action) handle their own clicks.
    if ((event.target as HTMLElement).closest('button')) return
    if (item.kind === 'TASK' && item.taskId) {
      onOpenTask?.(item)
      return
    }
    if (item.applicantId) {
      onOpenApplicant?.(item.applicantId, { newTab: event.metaKey })
    }
  }

  const isRowClickable =
    (item.kind === 'TASK' && Boolean(item.taskId)) || Boolean(item.applicantId)

  const row = (
    <div
      data-testid="agenda-row"
      role={isRowClickable ? 'button' : undefined}
      tabIndex={isRowClickable ? 0 : undefined}
      aria-label={
        isRowClickable
          ? item.kind === 'TASK'
            ? `Open task: ${item.subtitle}`
            : `Open ${item.heading}`
          : undefined
      }
      onClick={isRowClickable ? handleRowClick : undefined}
      onKeyDown={
        isRowClickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                if (item.kind === 'TASK' && item.taskId) onOpenTask?.(item)
                else if (item.applicantId)
                  onOpenApplicant?.(item.applicantId, { newTab: false })
              }
            }
          : undefined
      }
      className={cn(
        'flex flex-row items-center gap-2 rounded px-2 py-1.5 transition-opacity duration-300 hover:bg-doubleNickel-gray-50',
        isRowClickable && 'cursor-pointer',
        isDimmed && 'opacity-50'
      )}
    >
      {/* Time. Fixed width so every row's glyph lines up. */}
      <div className="w-[52px] flex-shrink-0">
        {isOverdue ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-medium text-doubleNickel-error-700">
              {item.overdueDueLabel}
            </span>
            <span className="text-[10px] text-doubleNickel-error-500">
              {item.overdueAgeLabel}
            </span>
          </div>
        ) : suppressTime ? null : (
          <span className="text-[11px] font-medium text-doubleNickel-gray-600">
            {dayjs(item.at).format('h:mm A')}
          </span>
        )}
      </div>

      {/* Priority (tasks) or calendar (bookings). Identity only — completion
          used to hide behind a hover swap here, which was too easy to miss. */}
      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
        <LeadingGlyph item={item} />
      </div>

      {/* Heading + subtitle. The heading is the only route to the applicant. */}
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        {item.applicantId ? (
          <button
            type="button"
            onClick={(e) =>
              onOpenApplicant?.(item.applicantId, { newTab: e.metaKey })
            }
            className={cn(
              'min-w-0 truncate text-left text-xs font-medium text-doubleNickel-gray-800 hover:text-doubleNickel-brand-500 hover:underline',
              isStruck && 'line-through'
            )}
          >
            {item.heading}
          </button>
        ) : (
          <span
            className={cn(
              'min-w-0 truncate text-xs font-medium text-doubleNickel-gray-800',
              isStruck && 'line-through'
            )}
          >
            {item.heading}
          </span>
        )}
        <span className="min-w-0 truncate text-[11px] text-doubleNickel-gray-500">
          {showOwner && (
            <>
              {/* Unowned work is real: `Task.assignees` can be empty when an
                  automated rule creates a task from a template with no static
                  assignee. It surfaces only on a company-wide agenda. */}
              <span
                className={cn(
                  'font-medium',
                  item.owner
                    ? 'text-doubleNickel-gray-700'
                    : 'text-doubleNickel-warning-700'
                )}
              >
                {item.owner?.name ?? 'Unassigned'}
              </span>
              {' · '}
            </>
          )}
          {item.subtitle}
        </span>
      </div>

      {/* Right-aligned in a slot of fixed width, so the pill forms a column
          rather than floating at the end of whatever the subtitle happened to
          be. Reserved on rows without a pill for the same reason. */}
      <div className="flex w-9 flex-shrink-0 flex-row items-center justify-end">
        {item.isAiBooked && (
          <ActorBadge
            variant="ai"
            label={`Booked by ${item.voiceAgentName ?? 'the AI voice agent'}`}
          />
        )}
      </div>

      {/* Complete slot, immediately left of the action. Always occupies its
          width — bookings leave it empty — so the action stays in one column
          across every row. Visible rather than hover-revealed: tucked behind a
          hover on the priority glyph, nobody found it. */}
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
        {canComplete && (
          <Tooltip label="Mark as complete" withArrow position="top">
            <button
              type="button"
              aria-label={`Mark "${item.heading}" as complete`}
              onClick={handleComplete}
              className="group flex h-6 w-6 items-center justify-center rounded hover:bg-doubleNickel-success-50"
            >
              <IconCheckmark
                className={cn(
                  ACTION_ICON_CLASS,
                  'stroke-doubleNickel-gray-400 group-hover:stroke-doubleNickel-success-600'
                )}
              />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Action slot. Always occupies its width so the icons form one column. */}
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
        {item.action !== 'NONE' && canAct && (
          <Tooltip
            label={item.action === 'CALL' ? 'Call' : 'Send an email'}
            withArrow
            position="top"
          >
            <button
              type="button"
              aria-label={
                item.action === 'CALL'
                  ? `Call ${item.heading}`
                  : `Email ${item.heading}`
              }
              onClick={() => onAction?.(item)}
              className="group flex h-6 w-6 items-center justify-center rounded hover:bg-doubleNickel-brand-25"
            >
              {item.action === 'CALL' ? (
                <IconPhone
                  className={cn(
                    ACTION_ICON_CLASS,
                    'stroke-doubleNickel-gray-400 group-hover:stroke-doubleNickel-brand-600'
                  )}
                />
              ) : (
                <IconEmail
                  className={cn(
                    ACTION_ICON_CLASS,
                    'stroke-doubleNickel-gray-400 group-hover:stroke-doubleNickel-brand-600'
                  )}
                />
              )}
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  )

  if (!hoverCard) return row

  return (
    <Tooltip label={hoverCard} withArrow position="top" openDelay={250}>
      {row}
    </Tooltip>
  )
}

export default AgendaRow
